import MatchCards from "@/components/KbvMatchCards";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import {
  LIVE_STATUSES,
  FINISHED_STATUSES,
  NOT_STARTED_STATUS,
} from "@/contants";
import MatchTime from "./MatchTime";
import OddsStatus from "./OddsStatus";
import MatchOddPopup from "./MatchOddPopup";
import { hasMatchOddsMarkets } from "@/components/OddsChangePopup/oddsPopupHelpers";
import { flattenMatchOdds } from "./slug";

const oddsPriceClass = (trend) => {
  const t = String(trend || "");
  if (t === "up" || t === "increased") return "text-green-600";
  if (t === "down" || t === "decreased") return "text-red-600";
  return "";
};

const handicapTexts = (handicapValue, horizontalLayout) => {
  const n = Number(handicapValue);
  if (Number.isNaN(n)) {
    return { homeHandicapText: "-", awayHandicapText: "-", isAwayHandicap: false };
  }
  const abs = Math.abs(n);
  const isAwayHandicap = n < 0;
  if (horizontalLayout) {
    return {
      homeHandicapText: !isAwayHandicap ? `-${abs}` : `+${abs}`,
      awayHandicapText: isAwayHandicap ? `-${abs}` : `+${abs}`,
      isAwayHandicap,
    };
  }
  return {
    homeHandicapText: !isAwayHandicap ? `-${abs}` : null,
    awayHandicapText: isAwayHandicap ? `-${abs}` : null,
    isAwayHandicap,
  };
};

function TeamRow({
  logo,
  name,
  alt,
  isWin,
  yellow,
  red,
  direction,
  isNotStarted,
  score,
  halfScore,
  nameClass,
}) {
  return (
    <div className="flex items-center justify-between gap-1">
      <div className="flex gap-1 min-w-0">
        {logo ? (
          <img src={logo} alt={alt} width={20} height={20} className="w-5 h-5 rounded-full object-contain shrink-0" />
        ) : (
          <span className="w-5 h-5 shrink-0" />
        )}
        <span className={`truncate text-sm ${isWin ? "font-bold" : ""} ${nameClass}`}>{name}</span>
        <MatchCards yellow={yellow} red={red} direction={direction} />
      </div>
      {!isNotStarted ? (
        <span className={`text-sm tabular-nums ${isWin ? "font-bold" : ""}`}>
          {score}
          <span className="ml-1 text-sm text-[var(--text-half-score)] font-normal">({halfScore})</span>
        </span>
      ) : null}
    </div>
  );
}

function OddsRow({
  horizontalLayout,
  handicapText,
  handicapClassName,
  trend,
  price,
}) {
  if (horizontalLayout) {
    return (
      <div className="kbv-ltd-odds-row is-horizontal">
        <span className={`text-sm text-right whitespace-nowrap ${handicapClassName || ""}`}>
          {handicapText ?? ""}
        </span>
        <div className="w-3 flex items-center justify-center">
          <OddsStatus trend={trend} />
        </div>
        <span className={`text-sm tabular-nums text-right ${oddsPriceClass(trend)}`}>{price}</span>
      </div>
    );
  }

  return (
    <div className="kbv-ltd-odds-row is-stacked">
      {handicapText ? (
        <span className={`text-sm ${handicapClassName || "kbv-ltd-hdp"}`}>
          {handicapText}
        </span>
      ) : null}
      <div className="flex items-center gap-1">
        <OddsStatus trend={trend} />
        <span className={`text-sm ${oddsPriceClass(trend)}`}>{price}</span>
      </div>
    </div>
  );
}

function OddsBlock({
  horizontalLayout,
  isAwayHandicap,
  homeHandicapText,
  awayHandicapText,
  homeOdds,
  awayOdds,
  homeTrend,
  awayTrend,
  match,
  withOddsPopup,
}) {
  const homeClass = horizontalLayout
    ? !isAwayHandicap
      ? "text-red-600"
      : "text-green-600"
    : undefined;
  const awayClass = horizontalLayout
    ? isAwayHandicap
      ? "text-red-600"
      : "text-green-600"
    : undefined;

  const rows = (
    <>
      <OddsRow
        horizontalLayout={horizontalLayout}
        handicapText={homeHandicapText}
        handicapClassName={homeClass}
        trend={homeTrend}
        price={homeOdds}
      />
      <OddsRow
        horizontalLayout={horizontalLayout}
        handicapText={awayHandicapText}
        handicapClassName={awayClass}
        trend={awayTrend}
        price={awayOdds}
      />
    </>
  );

  const inner = (
    <div className={`kbv-ltd-odds ${horizontalLayout ? "is-horizontal" : "is-stacked"}`}>
      {rows}
    </div>
  );

  return (
    <MatchOddPopup enabled={withOddsPopup} match={match}>
      {inner}
    </MatchOddPopup>
  );
}

function TeamsBlock({
  horizontalLayout,
  match,
  isNotStarted,
  isLive,
  isFinished,
  isHomeWin,
  isAwayWin,
  currentHomeScore,
  currentAwayScore,
  currentHomeHalfScore,
  currentAwayHalfScore,
  homeYellow,
  homeRed,
  awayYellow,
  awayRed,
}) {
  const homeName = match.homeTeam?.nameVi || match.homeTeam?.name || match.homeName || "";
  const awayName = match.awayTeam?.nameVi || match.awayTeam?.name || match.awayName || "";
  const homeLogo = match.homeTeam?.logo;
  const awayLogo = match.awayTeam?.logo;

  const upcomingMax = "max-w-[160px] max-[400px]:max-w-[120px]";
  const liveMax = "max-w-[120px] max-[400px]:max-w-[95px]";
  const nameMax = isNotStarted ? upcomingMax : isLive ? liveMax : "truncate";

  if (horizontalLayout) {
    return (
      <div className="kbv-ltd-teams is-horizontal text-[var(--theme-text)] cursor-default">
        <div className="kbv-ltd-team-home">
          <MatchCards yellow={homeYellow} red={homeRed} direction="away" />
          <span className={`text-sm truncate ${isHomeWin ? "font-bold" : ""}`}>{homeName}</span>
          {homeLogo ? (
            <img src={homeLogo} alt="" width={20} height={20} className="w-5 h-5 rounded-full object-contain shrink-0" />
          ) : (
            <span className="w-5 h-5 shrink-0" />
          )}
        </div>
        <span className="kbv-ltd-vs text-sm text-gray-700">
          {isNotStarted ? (
            <span className="font-semibold uppercase text-gray-700">VS</span>
          ) : (
            <span className="font-bold text-red-600 tabular-nums">
              {currentHomeScore} - {currentAwayScore}
            </span>
          )}
        </span>
        <div className="kbv-ltd-team-away">
          {awayLogo ? (
            <img src={awayLogo} alt="" width={20} height={20} className="w-5 h-5 rounded-full object-contain shrink-0" />
          ) : (
            <span className="w-5 h-5 shrink-0" />
          )}
          <span className={`text-[13px] max-w-[120px] truncate ${isAwayWin ? "font-bold" : ""}`}>
            {awayName}
          </span>
          <MatchCards yellow={awayYellow} red={awayRed} direction="home" />
        </div>
      </div>
    );
  }

  return (
    <div className="kbv-ltd-teams is-stacked text-[var(--theme-text)] cursor-default">
      <div className="flex-1 flex flex-col gap-1">
        <TeamRow
          logo={homeLogo}
          name={homeName}
          alt={match.homeName}
          isWin={isHomeWin}
          yellow={homeYellow}
          red={homeRed}
          direction="home"
          isNotStarted={isNotStarted}
          score={currentHomeScore}
          halfScore={currentHomeHalfScore}
          nameClass={isFinished ? "truncate" : nameMax}
        />
        <TeamRow
          logo={awayLogo}
          name={awayName}
          alt={match.awayName}
          isWin={isAwayWin}
          yellow={awayYellow}
          red={awayRed}
          direction="away"
          isNotStarted={isNotStarted}
          score={currentAwayScore}
          halfScore={currentAwayHalfScore}
          nameClass={isFinished ? "truncate" : nameMax}
        />
      </div>
    </div>
  );
}

export default function Match({ match: rawMatch, horizontalLayout = false, isLastItem = false }) {
  const match = flattenMatchOdds(rawMatch);
  const { getScore, getOdds } = useLiveDataForMatch(match.id);
  const liveScore = getScore();
  const liveOdds = getOdds();
  const filterOdds = useFilterOdds(match, liveOdds, null, -1);
  const odds = useMatchOdds(match, filterOdds, -1);

  const handicapValue = odds.handicapValue;
  const { homeHandicapText, awayHandicapText, isAwayHandicap } = handicapTexts(
    handicapValue,
    horizontalLayout,
  );

  const statusNum =
    liveScore?.status !== undefined ? Number(liveScore.status) : Number(match.status);
  const isLive = LIVE_STATUSES.includes(statusNum);
  const isFinished = FINISHED_STATUSES.includes(statusNum);
  const isNotStarted = NOT_STARTED_STATUS.includes(statusNum);

  const {
    currentHomeScore,
    currentAwayScore,
    currentHomeHalfScore,
    currentAwayHalfScore,
    isHomeWin,
    isAwayWin,
    homeYellow,
    homeRed,
    awayYellow,
    awayRed,
    currentHomeCorner,
    currentAwayCorner,
  } = useMatchData(liveScore, match);

  return (
    <div
      className={`test-12321 kbv-ltd-match-row ${horizontalLayout ? "is-horizontal" : "is-stacked"} bg-[var(--theme-foreground,transparent)]${
        isLastItem ? "" : " kbv-ltd-match-sep"
      }`}
    >
      <MatchTime
        homeCorner={currentHomeCorner}
        awayCorner={currentAwayCorner}
        isLive={isLive}
        isFinished={isFinished}
        isNotStarted={isNotStarted}
        matchTime={match.matchTime}
        liveScore={liveScore}
        status={statusNum}
        halfStartTime={match.halfStartTime}
        isHorizontal={horizontalLayout}
      />
      <TeamsBlock
        horizontalLayout={horizontalLayout}
        match={match}
        isNotStarted={isNotStarted}
        isLive={isLive}
        isFinished={isFinished}
        isHomeWin={isHomeWin}
        isAwayWin={isAwayWin}
        currentHomeScore={currentHomeScore}
        currentAwayScore={currentAwayScore}
        currentHomeHalfScore={currentHomeHalfScore}
        currentAwayHalfScore={currentAwayHalfScore}
        homeYellow={homeYellow}
        homeRed={homeRed}
        awayYellow={awayYellow}
        awayRed={awayRed}
      />
      {!isFinished ? (
        <OddsBlock
          horizontalLayout={horizontalLayout}
          isAwayHandicap={isAwayHandicap}
          homeHandicapText={homeHandicapText}
          awayHandicapText={awayHandicapText}
          homeOdds={odds.homeHandicapOdds}
          awayOdds={odds.awayHandicapOdds}
          homeTrend={odds.homeHandicapTrend}
          awayTrend={odds.awayHandicapTrend}
          match={match}
          withOddsPopup={hasMatchOddsMarkets(filterOdds)}
        />
      ) : null}
    </div>
  );
}
